<template>
    <div
            id="property-search-header"
            class="property-search-header flex-column sticky-top p-sm-3"
    >
        <b-container fluid>
            <b-row>
                <b-col
                        cols="auto"
                        class="logo-container p-0 align-items-center"
                >
                    <property-mls-sponsor-logo />
                </b-col>
                <b-col class="property-search-wrapper">
                    <div
                            class="d-flex w-100"
                            :class="{
                                'mb-sm-3': expanded && showFilters,
                                'header-row': tabDisplay && showFilters || !isMobile,
                                'no-tabs': !tabDisplay,
                            }"
                    >
                        <div
                                class="search-container d-flex"
                                :class="{
                                    'w-70': !displaySearchInput,
                                }"
                                v-if="showFilters || !isMobile"
                        >
                            <b-input-group
                                    v-if="displaySearchInput"
                                    class="h-100 min-vw-10"
                            >
                                <property-search-input
                                        class="mx-1 flex-nowrap"
                                        @input="handleSearchInput"
                                        v-model="searchInputValue"
                                />
                            </b-input-group>
                            <b-button
                                    variant="secondary"
                                    :class="{
                                        'd-flex d-md-none justify-content-center align-items-center': true,
                                        'w-100': !displaySearchInput,
                                    }"
                                    @click="handleOpenFiltersModal"
                            >
                                <i
                                        class="far fa-sliders-h text-black"
                                        :class="{active: filtersActive}"
                                />
                                {{ filterLabel }}
                            </b-button>
                        </div>
                        <template v-if="!tabDisplay">
                            <property-search-filters
                                    :primary-filters="primaryFilters"
                                    :display-save-search="false"
                                    v-model="filter"
                                    v-if="expanded"
                            />
                            <b-button
                                    variant="secondary"
                                    :class="{
                                        'ml-1 d-flex d-md-none justify-content-center align-items-center': true,
                                        'w-30 h-100': !displaySearchInput,
                                    }"
                                    @click="toggleMap"
                                    v-if="isMobile"
                            >
                                <i
                                        class="far mr-2"
                                        :class="{
                                            'fa-map': !renderMap,
                                            'fa-th-large': renderMap,
                                        }"
                                />
                                {{ renderMap ? 'List' : 'Map' }}
                            </b-button>
                            <b-dropdown
                                    right
                                    no-caret
                                    class="property-saved-searches-save-button text-center"
                                    menu-class="p-0 w-100"
                                    toggle-class="text-truncate d-flex align-items-center align-content-center
                                    shadow-none justify-content-center"
                                    ref="saveSearchDropdown"
                                    disabled
                            >
                                <template v-slot:button-content>
                                    <h3
                                            class="mb-0 font-weight-bold text-truncate"
                                            @click="handleAuthReqButtonClick"
                                    >
                                        <i class="far mr-2 fa-bell" />
                                        Save Search
                                    </h3>
                                </template>
                            </b-dropdown>
                        </template>
                        <property-search-header-tab
                                v-else
                                @PROPERTY_SEARCH_TAB_CHANGED="handleTabChanged($event)"
                        />
                    </div>

                    <property-search-filters
                            :primary-filters="primaryFilters"
                            v-model="filter"
                            v-if="expanded && tabDisplay && showFilters"
                    />
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>
<script>
    import './PropertySearchHeader.scss';
    import {debounce} from "lodash/function";
    import IsMobileMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/IsMobileMixin";
    import {FILTERS_MODAL_OPEN} from "../../Modals/FiltersModal/FiltersModal";
    import PropertySearchFilters from "../GroupPropertySearchFilters/PropertySearchFilters/PropertySearchFilters";
    import PropertySearchInput from "./PropertySearchInput/PropertySearchInput";
    import ShowNotificationMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin";
    import PropertyMlsSponsorLogo from "../../PropertyDetails/PropertyMlsSponsor/PropertyMlsSponsorLogo";

    import PropertySearchMixin, {
        PROPERTY_SEARCH_MAP_FIT_BOUNDS,
        PROPERTY_SEARCH_HEADER_UPDATING_PROPERTIES,
        PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED,
        PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED_MAP,
        PROPERTY_SEARCH_HEADER_CLEAR_LABELS,
        PAGE_SIZE,
        MAP_PAGE_SIZE,
        CLEAR_FILTER_EVENT,
        PROPERTY_SEARCH_MAP_DISPLAY,
        PROPERTY_SEARCH_MAP_TOGGLE,
        PROPERTY_SEARCH_RESET,
        PROPERTY_SEARCH_START_SEARCHING_DISPLAY,
        PROPERTY_SEARCH_DISABLED_OPTION,
    } from './../../../mixins/PropertySearchMixin';

    import {
        TABS,
        PROPERTY_SEARCH_HEADER_ACCESS_HELP_CENTER,
        PROPERTY_SEARCH_HEADER_CLOSE_SEARCH_APP,
        PROPERTY_SEARCH_SAVED_SEARCHES_DISPLAY,
        PROPERTY_SEARCH_TAB_CHANGED,
        PROPERTY_SEARCH_TAB_RESET
    } from "./PropertySearchHeaderTab/PropertySearchHeaderTab";
    import {PROPERTY_SAVED_SEARCHES_DISPLAY_MAP} from "../../GroupPropertySavedSearches/PropertySavedSearches";
    import {PROPERTY_SEARCH_ACTIONS} from "../../../../helpers/variables";
    import {buildFilterDataObjectFromFilter} from "../../../../helpers/functions";
    import {GLOBAL_EVENTS} from '../../../../helpers/globalEvents';

    export {
        PROPERTY_SEARCH_MAP_FIT_BOUNDS,
        PROPERTY_SEARCH_HEADER_UPDATING_PROPERTIES,
        PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED,
        PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED_MAP,
        PROPERTY_SEARCH_HEADER_CLEAR_LABELS,
        PAGE_SIZE,
        MAP_PAGE_SIZE,
        CLEAR_FILTER_EVENT,
        PROPERTY_SEARCH_MAP_DISPLAY,
        PROPERTY_SEARCH_MAP_TOGGLE,
        PROPERTY_SEARCH_RESET,
        PROPERTY_SEARCH_START_SEARCHING_DISPLAY,
        PROPERTY_SEARCH_DISABLED_OPTION,
        TABS,
        PROPERTY_SEARCH_HEADER_ACCESS_HELP_CENTER,
        PROPERTY_SEARCH_HEADER_CLOSE_SEARCH_APP,
        PROPERTY_SEARCH_SAVED_SEARCHES_DISPLAY,
    };

    export default {
        name: 'PropertySearchHeader',
        components: {
            PropertySearchInput,
            PropertySearchFilters,
            PropertyMlsSponsorLogo,
            PropertySearchHeaderTab: () => {
                return import("./PropertySearchHeaderTab/PropertySearchHeaderTab");
            },
        },
        mixins: [
            IsMobileMixin,
            PropertySearchMixin,
            ShowNotificationMixin
        ],
        props: {
            primaryFilters: {
                type: Array,
                default: () => [],
            },
            tabDisplay: {
                type: Boolean,
                default: true,
            },
            expanded: {
                type: Boolean,
                default: false,
            },
            filterable: {
                type: Boolean,
                default: true,
            },
            toggleable: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                renderMap: true,
                favoritesTabActive: false,
                savedSearchTabActive: false,
            };
        },
        computed: {
            displaySearchInput() {
                return this.tabDisplay || this.windowWidth > 575;
            },
            filterLabel() {
                return this.tabDisplay ? '' : 'Filters';
            },
            showFilters() {
                return !this.favoritesTabActive && !this.savedSearchTabActive;
            },
            filtersActive() {
                return Boolean(this.filter.price.min.value > 0 ||
                    this.filter.price.max.value !== null ||
                    this.filter.beds.value ||
                    this.filter.baths.value ||
                    this.filter.homeType.length ||
                    this.filter.sale.length ||
                    this.filter.keywords.length ||
                    this.filter.squareFeet.min.value > 0 ||
                    this.filter.squareFeet.max.value !== null ||
                    this.filter.lotSize.min.value > 0 ||
                    this.filter.lotSize.max.value !== null ||
                    this.filter.yearBuilt.min.value > 0 ||
                    this.filter.yearBuilt.max.value !== null);
            }
        },
        beforeMount() {
            this.renderMap = this.tabDisplay || !this.isMobile;
        },
        methods: {
            handleTabChanged(e) {
                [this.favoritesTabActive, this.savedSearchTabActive] = e || [false, false];
            },

            toggleMap() {
                this.$bus.$emit(PROPERTY_SEARCH_MAP_TOGGLE, this.renderMap = !this.renderMap);
            },

            handleAuthReqButtonClick() {
                this.$bus.$emit(
                    PROPERTY_SEARCH_DISABLED_OPTION,
                    PROPERTY_SEARCH_ACTIONS.SAVE_SEARCH,
                    {
                        filter: this.filtersActive ? buildFilterDataObjectFromFilter(this.filter) : null
                    }
                );
            },

            handleSearchInput: debounce(function (searchInput) {
                this.$bus.$emit(CLEAR_FILTER_EVENT);
                this.$bus.$emit(PROPERTY_SEARCH_RESET);
                this.$bus.$emit(PROPERTY_SAVED_SEARCHES_DISPLAY_MAP);
                this.favoritesTabActive = false;
                this.savedSearchTabActive = false;
                this.filter.location = null;
                this.filter.search = searchInput.value;
                this.filter.searchLabel = searchInput.label;
                this.searchInputValue.value = searchInput.value;
                this.searchInputValue.label = searchInput.label;
                if (searchInput) {
                    this.$bus.$emit(GLOBAL_EVENTS.HOMEOWNER_SEARCHED_PROPERTIES, searchInput.value);
                }
            }, 750),

            handleOpenFiltersModal() {
                this.$bus.$emit(FILTERS_MODAL_OPEN, !this.displaySearchInput);
            },
        },
        destroyed() {
            localStorage.removeItem(this.filter.getParameterByName("projectUuid"));
            this.filter.initParams();
        },
    };
</script>
